import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import { Logo } from "./Logo";
import { useSite } from "@/lib/SiteContext";
import { isPlaceholder } from "@/lib/content";

const quickLinks = [
  { to: "/", label: "Home" }, { to: "/about", label: "About" }, { to: "/services", label: "Services" },
  { to: "/residential", label: "Residential Cleaning" }, { to: "/commercial", label: "Commercial Cleaning" },
  { to: "/areas", label: "Service Areas" },
  { to: "/contact", label: "Contact" }, { to: "/quote", label: "Request a Quote" },
];

const serviceLinks = [
  { to: "/residential", label: "Residential Cleaning" },
  { to: "/commercial", label: "Commercial Cleaning" },
  { to: "/commercial", label: "Office Cleaning" },
  { to: "/specialised", label: "Medical Cleaning" },
  { to: "/specialised", label: "End of Lease Cleaning" },
  { to: "/specialised", label: "Deep Cleaning" },
  { to: "/specialised", label: "Industrial Cleaning" },
  { to: "/specialised", label: "Childcare & Education Cleaning" },
];

export default function Footer() {
  const { settings } = useSite();
  return (
    <footer className="bg-navy text-slate-300">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:grid-cols-2 sm:px-6 lg:grid-cols-4 lg:px-8">
        <div>
          <Logo dark />
          <p className="mt-5 text-sm leading-relaxed text-slate-400">
            Professional Residential &amp; Commercial Cleaning across Perth, Western Australia.
          </p>
          <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.25em] text-teal-400">
            Professional · Reliable · Thorough
          </p>
        </div>
        <nav aria-label="Quick links">
          <h3 className="font-display text-sm font-bold uppercase tracking-wider text-white">Quick Links</h3>
          <ul className="mt-5 space-y-2.5 text-sm">
            {quickLinks.map((l) => (
              <li key={l.label}>
                <Link to={l.to} className="focus-ring rounded transition-colors hover:text-teal-300" data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>{l.label}</Link>
              </li>
            ))}
          </ul>
        </nav>
        <nav aria-label="Services">
          <h3 className="font-display text-sm font-bold uppercase tracking-wider text-white">Services</h3>
          <ul className="mt-5 space-y-2.5 text-sm">
            {serviceLinks.map((l) => (
              <li key={l.label}>
                <Link to={l.to} className="focus-ring rounded transition-colors hover:text-teal-300" data-testid={`footer-service-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>{l.label}</Link>
              </li>
            ))}
          </ul>
        </nav>
        <div>
          <h3 className="font-display text-sm font-bold uppercase tracking-wider text-white">Contact</h3>
          <ul className="mt-5 space-y-3 text-sm">
            <li className="flex items-center gap-2.5">
              <Phone className="h-4 w-4 shrink-0 text-teal-400" />
              {isPlaceholder(settings.phone) ? (
                <span data-testid="footer-phone">{settings.phone}</span>
              ) : (
                <a href={`tel:${settings.phone.replace(/\s/g, "")}`} className="focus-ring rounded hover:text-teal-300" data-testid="footer-phone">{settings.phone}</a>
              )}
            </li>
            <li className="flex items-center gap-2.5">
              <Mail className="h-4 w-4 shrink-0 text-teal-400" />
              {isPlaceholder(settings.email) ? (
                <span data-testid="footer-email">{settings.email}</span>
              ) : (
                <a href={`mailto:${settings.email}`} className="focus-ring rounded hover:text-teal-300" data-testid="footer-email">{settings.email}</a>
              )}
            </li>
            <li className="flex items-center gap-2.5">
              <MapPin className="h-4 w-4 shrink-0 text-teal-400" />
              <span>Perth, WA</span>
            </li>
          </ul>
          <Link to="/quote" className="focus-ring mt-6 inline-block rounded-full bg-teal-600 px-6 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-teal-500" data-testid="footer-quote-button">
            Get a Free Quote
          </Link>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 py-6 text-xs text-slate-500 sm:flex-row sm:px-6 lg:px-8">
          <p>© 2026 RD Cleaning Services{settings.abn ? ` · ABN ${settings.abn}` : ""}. All Rights Reserved.</p>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="focus-ring rounded transition-colors hover:text-teal-300" data-testid="footer-privacy-link">Privacy Policy</Link>
            <Link to="/terms" className="focus-ring rounded transition-colors hover:text-teal-300" data-testid="footer-terms-link">Terms &amp; Conditions</Link>
            <Link to="/admin" className="focus-ring rounded transition-colors hover:text-teal-300" data-testid="footer-admin-link">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
